import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { meldVakmanAan } from "@/lib/vakman.functions";
import { VAKMAN_CATEGORIEEN, LEAD_PRIJS, MAX_KOPERS } from "@/lib/vakman-shared";
import { VakmanShell, Veld, invoer, euro } from "@/components/vakman";

export const Route = createFileRoute("/vakman/aanmelden")({
  head: () => ({
    meta: [
      { title: "Aanmelden als elektricien | offerto" },
      { name: "description", content: `Ontvang offerteaanvragen in jouw regio. Ontgrendel contactgegevens voor ${LEAD_PRIJS} euro per aanvraag.` },
      { property: "og:title", content: "Aanmelden als elektricien | offerto" },
      { property: "og:description", content: "Ontvang offerteaanvragen voor elektra-klussen in jouw regio." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Aanmelden,
});

function Aanmelden() {
  const meld = useServerFn(meldVakmanAan);
  const [f, setF] = useState({ bedrijfsnaam: "", kvk: "", voornaam: "", achternaam: "", telefoon: "", email: "", wachtwoord: "", plaats: "", straal_km: 25 });
  const [cats, setCats] = useState<string[]>([]);
  const [fout, setFout] = useState("");
  const [bezig, setBezig] = useState(false);
  const [klaar, setKlaar] = useState(false);
  const zet = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF({ ...f, [k]: k === "straal_km" ? Number(e.target.value) : e.target.value });

  async function verstuur(e: React.FormEvent) {
    e.preventDefault();
    if (cats.length === 0) return setFout("Kies minstens één soort klus.");
    if (f.wachtwoord.length < 8) return setFout("Kies een wachtwoord van minstens 8 tekens.");
    setBezig(true);
    setFout("");
    try {
      const res = await meld({ data: { ...f, categorieen: cats } });
      if (!res.ok) setFout(res.fout);
      else setKlaar(true);
    } catch {
      setFout("Controleer je gegevens en probeer het opnieuw.");
    }
    setBezig(false);
  }

  if (klaar) {
    return (
      <VakmanShell>
        <div className="rounded-xl border border-border bg-background p-6">
          <h1 className="text-2xl font-bold">Bedankt voor je aanmelding</h1>
          <p className="mt-2 text-muted-foreground">We controleren je gegevens. Zodra je account is goedgekeurd kun je inloggen en aanvragen bekijken.</p>
          <Link to="/vakman" className="mt-5 inline-block rounded-md bg-primary px-5 py-3 font-bold text-primary-foreground">Naar inloggen</Link>
        </div>
      </VakmanShell>
    );
  }

  return (
    <VakmanShell>
      <div className="rounded-xl border border-border bg-background p-6">
        <h1 className="text-2xl font-bold">Aanmelden als vakman</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Ontvang aanvragen in jouw regio. Je ziet eerst de klus, plaats en foto's. Contactgegevens ontgrendel je voor {euro(LEAD_PRIJS)} per aanvraag, met maximaal {MAX_KOPERS} vakmannen per aanvraag.
        </p>
        <form onSubmit={(e) => void verstuur(e)} className="mt-6 space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Veld label="Bedrijfsnaam"><input required value={f.bedrijfsnaam} onChange={zet("bedrijfsnaam")} className={invoer} /></Veld>
            <Veld label="KvK-nummer"><input value={f.kvk} onChange={zet("kvk")} inputMode="numeric" className={invoer} /></Veld>
            <Veld label="Voornaam"><input required value={f.voornaam} onChange={zet("voornaam")} className={invoer} /></Veld>
            <Veld label="Achternaam"><input required value={f.achternaam} onChange={zet("achternaam")} className={invoer} /></Veld>
            <Veld label="Telefoon"><input required type="tel" value={f.telefoon} onChange={zet("telefoon")} className={invoer} /></Veld>
            <Veld label="E-mailadres"><input required type="email" autoComplete="username" value={f.email} onChange={zet("email")} className={invoer} /></Veld>
          </div>
          <Veld label="Wachtwoord (minstens 8 tekens)"><input required type="password" autoComplete="new-password" value={f.wachtwoord} onChange={zet("wachtwoord")} className={invoer} /></Veld>
          <div className="grid gap-4 sm:grid-cols-2">
            <Veld label="Vestigingsplaats"><input required value={f.plaats} onChange={zet("plaats")} className={invoer} /></Veld>
            <Veld label="Werkgebied">
              <select value={f.straal_km} onChange={zet("straal_km")} className={invoer}>
                {[10, 15, 25, 40, 60, 100].map((k) => <option key={k} value={k}>Tot {k} km</option>)}
              </select>
            </Veld>
          </div>
          <fieldset>
            <legend className="text-sm font-bold">Welke klussen doe je?</legend>
            <div className="mt-2 space-y-2">
              {VAKMAN_CATEGORIEEN.map((c) => (
                <label key={c} className="flex items-center gap-3 rounded-md border border-border px-3 py-2.5">
                  <input type="checkbox" checked={cats.includes(c)} onChange={(e) => setCats(e.target.checked ? [...cats, c] : cats.filter((x) => x !== c))} className="h-5 w-5" />
                  {c}
                </label>
              ))}
            </div>
          </fieldset>
          {fout && <p className="text-sm font-medium text-destructive">{fout}</p>}
          <button disabled={bezig} className="w-full rounded-md bg-primary px-6 py-3 font-bold text-primary-foreground disabled:opacity-40">{bezig ? "Bezig…" : "Aanmelden"}</button>
        </form>
        <p className="mt-5 text-sm">Heb je al een account? <Link to="/vakman" className="font-bold underline">Inloggen</Link></p>
      </div>
    </VakmanShell>
  );
}
